import { History, RefreshCw } from "@ews-admin/global-design-system";
import React from "react";
import { useTranslation } from "react-i18next";
import { DoctorLicenceGrantHistoryEntry } from "../../types/doctorAdmin.definition";
import { LicenceStatus } from "../../types/enums";
import { formatDateTime } from "../../utils/date-format-utils";

interface GrantHistorySectionProps {
  history: DoctorLicenceGrantHistoryEntry[];
  loading: boolean;
}

export const GrantHistorySection: React.FC<GrantHistorySectionProps> = ({
  history,
  loading,
}) => {
  const { t } = useTranslation();

  const statusLabel = (status: LicenceStatus | null) =>
    status
      ? t(`admin.doctors.grant.status.${status}`, { defaultValue: status })
      : "—";

  return (
    <div className="page-section card-container">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("admin.doctors.grant.history.title")}
        </h3>
        <History className="w-5 h-5 text-gray-400" />
      </div>

      {loading ? (
        <div className="py-8 text-center">
          <RefreshCw className="mx-auto w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-sm font-medium text-neutral-700">
            {t("admin.doctors.grant.history.empty")}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.grant.history.table.grantedBy")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.grant.history.table.date")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.grant.history.table.statusChange")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.grant.history.table.period")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.grant.history.table.reason")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {history.map(entry => (
                <tr key={entry.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 text-neutral-800">
                    {entry.grantedByName ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {formatDateTime(entry.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {statusLabel(entry.previousStatus)}
                    {" → "}
                    {statusLabel(entry.newStatus)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {formatDateTime(entry.startDate)}
                    {" – "}
                    {formatDateTime(entry.endDate)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {entry.reason ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
